import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { Globe, RotateCcw, SlidersHorizontal, ToggleLeft } from "@fleet/icons";
import { Badge } from "@fleet/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@fleet/ui/card";
import { Skeleton } from "@fleet/ui/skeleton";
import { Switch } from "@fleet/ui/switch";
import { Label, TextField } from "@fleet/ui/text-field";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { appConfigQueryKeys, type components } from "@agape/shared";
import { LOCALE_DEFINITIONS } from "@agape/config/locales";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ErrorModel = components["schemas"]["ErrorModel"];
type AdminConfig = components["schemas"]["AdminConfigResponse"];
type OperationalSettings = components["schemas"]["OperationalSettings"];

/** Read path of each logical key within `OperationalSettings`. */
const READ: Record<string, (s: OperationalSettings) => number> = {
  "limits.rate_limit_rpm": (s) => s.limits.rateLimitRpm,
  "limits.avatar_max_upload_bytes": (s) => s.limits.avatarMaxUploadBytes,
  "limits.avatar_max_uploads_per_hour": (s) => s.limits.avatarMaxUploadsPerHour,
  "retention.trash_purge_days": (s) => s.retention.trashPurgeDays,
  "retention.activity_days": (s) => s.retention.activityDays,
  "retention.activity_recent_max_per_entity": (s) => s.retention.activityRecentMaxPerEntity,
  "retention.account_deletion_grace_days": (s) => s.retention.accountDeletionGraceDays,
  "retention.audit_log_days": (s) => s.retention.auditLogDays,
  "security.login_max_failed_attempts": (s) => s.security.loginMaxFailedAttempts,
  "security.login_lockout_secs": (s) => s.security.loginLockoutSecs,
  "domain.meeting_default_duration_minutes": (s) => s.domain.meetingDefaultDurationMinutes,
  "domain.inventory_low_stock_threshold": (s) => s.domain.inventoryLowStockThreshold,
};

const BOOL_READ: Record<string, (s: OperationalSettings) => boolean> = {
  "modules.p2p_enabled": (s) => s.modules.p2pEnabled,
  "modules.avatar_uploads_enabled": (s) => s.modules.avatarUploadsEnabled,
  "domain.prayer_private_by_default": (s) => s.domain.prayerPrivateByDefault,
  "domain.prayer_force_private": (s) => s.domain.prayerForcePrivate,
  "domain.reflection_followup_enabled": (s) => s.domain.reflectionFollowupEnabled,
};

/** Numeric groups of the panel, in the order they are painted. */
const GROUPS = ["limits", "retention", "security", "domain"] as const;

/**
 * PLATFORM operational configuration panel (CONFIG_OVER_CODE).
 *
 * Hot-edits the SAME source as the env: each change creates a persisted
 * override (`app_config`, scope `platform`) that overrides the deployment
 * default; "Reset to env" deletes the override and hands control back to the
 * env (or, if there is no env, to the code default). None of this requires a
 * redeploy.
 *
 * The panel shows, for each key, the EFFECTIVE value, its ORIGIN (env/default
 * or override) and the equivalent environment variable — so there are not two
 * vocabularies: `AVATAR_MAX_UPLOAD_MB` (env, megabytes) and
 * `limits.avatar_max_upload_bytes` (override, bytes) are the SAME option, and
 * the panel says so explicitly in the unit label.
 *
 * Keys marked as `org` are the **default and the ceiling** of each
 * congregation: whatever is touched here is what churches that have
 * configured nothing inherit, and the maximum the others can request. The
 * panel says so with a badge, so nobody believes they are editing ONE church.
 */
export function AppConfigManager() {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();
  const canWrite = () => can("settings.write");

  const query = createApiQuery<AdminConfig, ErrorModel>(
    () => appConfigQueryKeys.admin(),
    async () => api.GET("/api/admin/config", {}),
    () => ({ staleTime: 5000 }),
  );

  const upsertMutation = createApiMutation(async (vars: { key: string; value: unknown }) =>
    api.POST("/api/admin/config", { body: vars }),
  );

  const deleteMutation = createApiMutation(async (vars: { key: string }) =>
    api.DELETE("/api/admin/config/{key}", { params: { path: { key: vars.key } } }),
  );

  const resolved = () => query.data?.resolved;
  const envDefaults = () => query.data?.envDefaults;
  const overrides = createMemo(() => query.data?.overrides ?? []);
  const overriddenKeys = createMemo(() => new Set(overrides().map((o) => o.key)));
  const catalog = createMemo(() => query.data?.catalog ?? []);

  const refetch = () => {
    void query.refetch();
  };

  const saveOverride = (key: string, value: unknown) => {
    upsertMutation.mutate(
      { key, value },
      {
        onSuccess: () => {
          refetch();
          showToast(t(msg`Setting saved — applied live`), "success");
        },
        onError: () => {
          showToast(t(msg`Could not save the setting`), "error");
        },
      },
    );
  };

  const resetOverride = (key: string) => {
    deleteMutation.mutate(
      { key },
      {
        onSuccess: () => {
          refetch();
          showToast(t(msg`Setting reset to its environment default`), "success");
        },
      },
    );
  };

  const groupTitle = (group: string): string => {
    switch (group) {
      case "limits": {
        return t(msg`Operational limits`);
      }
      case "retention": {
        return t(msg`Retention & purge`);
      }
      case "domain": {
        return t(msg`Congregation defaults`);
      }
      default: {
        return t(msg`Login security`);
      }
    }
  };

  const groupHint = (group: string): string => {
    if (group === "domain" || group === "retention") {
      return t(
        msg`Some of these are congregation-level: what you set here is the default every church inherits and the ceiling none of them can exceed.`,
      );
    }
    return "";
  };

  const numericKeys = (group: string) =>
    catalog().filter((spec) => spec.kind === "int" && spec.key.startsWith(`${group}.`));

  const localePolicy = () => resolved()?.i18n.availableLocales ?? [];

  const toggleLocale = (code: string) => {
    const current = localePolicy();
    const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
    saveOverride("i18n.available_locales", next);
  };

  return (
    <div class="space-y-6" data-testid="app-config-manager">
      <div class="flex items-center gap-3">
        <SlidersHorizontal class="h-8 w-8 text-primary" />
        <div>
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Operational config`)}</h2>
          <p class="text-sm text-muted-foreground">
            {t(
              msg`Limits, retention, login policy, languages, module toggles and the defaults every congregation inherits. Environment variables are the deployment defaults; every change here overrides them live, with no redeploy.`,
            )}
          </p>
        </div>
      </div>

      {/* «No hay respuesta» ≠ «no hay nada» (P23/CC-60 · I3). */}
      <Show when={query.isError}>
        <QueryErrorState testId="admin-config-error" compact onRetry={() => void query.refetch()} />
      </Show>

      <Show when={!query.isError}>
        <Show
          when={!query.isLoading && resolved()}
          fallback={
            <div class="space-y-3">
              <Skeleton class="h-24 w-full rounded-xl" />
              <Skeleton class="h-48 w-full rounded-xl" />
            </div>
          }
        >
          {/* ── Grupos numéricos ─────────────────────────────────────── */}
          <For each={GROUPS}>
            {(group) => (
              <Show when={numericKeys(group).length > 0}>
                <Card data-testid={`config-group-${group}`}>
                  <CardHeader>
                    <CardTitle>{groupTitle(group)}</CardTitle>
                    <Show when={groupHint(group)}>
                      <p class="text-xs text-muted-foreground">{groupHint(group)}</p>
                    </Show>
                  </CardHeader>
                  <CardContent class="grid gap-4 sm:grid-cols-2">
                    <For each={numericKeys(group)}>
                      {(spec) => {
                        const read = READ[spec.key];
                        const effective = () => {
                          const s = resolved();
                          return read && s ? read(s) : 0;
                        };
                        const fromEnv = () => {
                          const s = envDefaults();
                          return read && s ? read(s) : 0;
                        };
                        const isOverridden = () => overriddenKeys().has(spec.key);
                        return (
                          <div class="space-y-1">
                            <div class="flex items-center justify-between gap-2">
                              <Label for={`config-${spec.key}`} class="text-xs font-mono">
                                {spec.key}
                              </Label>
                              <div class="flex items-center gap-1">
                                <Show when={spec.level === "org"}>
                                  <Badge variant="outline" data-testid={`level-${spec.key}`}>
                                    {t(msg`per congregation`)}
                                  </Badge>
                                </Show>
                                <Show
                                  when={isOverridden()}
                                  fallback={
                                    <Badge variant="secondary" data-testid={`origin-${spec.key}`}>
                                      {t(msg`env / default`)}
                                    </Badge>
                                  }
                                >
                                  <Badge data-testid={`origin-${spec.key}`}>
                                    {t(msg`override`)}
                                  </Badge>
                                </Show>
                              </div>
                            </div>
                            <TextField
                              id={`config-${spec.key}`}
                              type="number"
                              value={String(effective())}
                              disabled={!canWrite()}
                              data-testid={`config-value-${spec.key}`}
                              onChange={(e) => {
                                const raw = e.currentTarget.value.trim();
                                const parsed = Number.parseInt(raw, 10);
                                if (Number.isNaN(parsed) || parsed === effective()) {
                                  return;
                                }
                                saveOverride(spec.key, parsed);
                              }}
                            />
                            <p class="text-[11px] text-muted-foreground">
                              {/* Unidad SIEMPRE explícita: el env y el override
                                  pueden hablar en unidades distintas (MB vs bytes)
                                  y eso tiene que estar escrito, no adivinado. */}
                              {spec.unit} ·{" "}
                              <span class="font-mono">
                                {spec.env}
                                {spec.envUnit ? ` (${spec.envUnit})` : ""}
                              </span>{" "}
                              = {fromEnv()}
                            </p>
                            <Show when={isOverridden()}>
                              <button
                                type="button"
                                class="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                                disabled={!canWrite()}
                                data-testid={`reset-${spec.key}`}
                                onClick={() => {
                                  resetOverride(spec.key);
                                }}
                              >
                                <RotateCcw class="h-3 w-3" />
                                {t(msg`Reset to env`)}
                              </button>
                            </Show>
                          </div>
                        );
                      }}
                    </For>
                  </CardContent>
                </Card>
              </Show>
            )}
          </For>

          {/* ── Zona horaria por defecto de las reuniones (texto libre) ── */}
          <For each={catalog().filter((spec) => spec.kind === "str")}>
            {(spec) => {
              const effective = () => resolved()?.domain.meetingDefaultTimezone ?? "";
              return (
                <Card data-testid={`config-str-${spec.key}`}>
                  <CardHeader>
                    <CardTitle class="text-base font-mono">{spec.key}</CardTitle>
                  </CardHeader>
                  <CardContent class="space-y-1">
                    <TextField
                      id={`config-${spec.key}`}
                      value={effective()}
                      disabled={!canWrite()}
                      data-testid={`config-value-${spec.key}`}
                      onChange={(e) => {
                        const raw = e.currentTarget.value.trim();
                        if (raw.length === 0 || raw === effective()) {
                          return;
                        }
                        saveOverride(spec.key, raw);
                      }}
                    />
                    <p class="text-[11px] text-muted-foreground">
                      {spec.unit} · <span class="font-mono">{spec.env}</span>
                    </p>
                  </CardContent>
                </Card>
              );
            }}
          </For>

          {/* ── Toggles de módulo y de política ──────────────────────── */}
          <Card data-testid="config-group-modules">
            <CardHeader>
              <CardTitle class="flex items-center gap-2">
                <ToggleLeft class="h-5 w-5 text-muted-foreground" />
                {t(msg`Modules & policies`)}
              </CardTitle>
            </CardHeader>
            <CardContent class="space-y-4">
              <For each={catalog().filter((spec) => spec.kind === "bool")}>
                {(spec) => {
                  const read = BOOL_READ[spec.key];
                  const checked = () => {
                    const s = resolved();
                    return read && s ? read(s) : false;
                  };
                  return (
                    <div class="flex items-center justify-between gap-4">
                      <div class="min-w-0">
                        <Label class="font-mono text-xs">{spec.key}</Label>
                        <p class="text-[11px] text-muted-foreground font-mono">{spec.env}</p>
                      </div>
                      <div class="flex items-center gap-3">
                        <Show when={spec.level === "org"}>
                          <Badge variant="outline">{t(msg`per congregation`)}</Badge>
                        </Show>
                        <Show when={overriddenKeys().has(spec.key)}>
                          <button
                            type="button"
                            class="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                            disabled={!canWrite()}
                            data-testid={`reset-${spec.key}`}
                            onClick={() => {
                              resetOverride(spec.key);
                            }}
                          >
                            <RotateCcw class="h-3 w-3" />
                            {t(msg`Reset to env`)}
                          </button>
                        </Show>
                        <span data-testid={`config-switch-${spec.key}`}>
                          <Switch
                            checked={checked()}
                            disabled={!canWrite() || upsertMutation.isPending}
                            onCheckedChange={(v) => {
                              saveOverride(spec.key, v);
                            }}
                          />
                        </span>
                      </div>
                    </div>
                  );
                }}
              </For>
            </CardContent>
          </Card>

          {/* ── Política de idioma (plataforma sobre preferencia de usuario) ── */}
          <Card data-testid="config-group-i18n">
            <CardHeader>
              <CardTitle class="flex items-center gap-2">
                <Globe class="h-5 w-5 text-muted-foreground" />
                {t(msg`Available languages`)}
              </CardTitle>
            </CardHeader>
            <CardContent class="space-y-4">
              <p class="text-xs text-muted-foreground">
                {t(
                  msg`Languages users may pick. With none selected there is no restriction: every language stays available. The server enforces this — a client cannot keep a language you removed.`,
                )}
              </p>
              <div class="flex flex-wrap gap-2">
                <For each={LOCALE_DEFINITIONS}>
                  {(def) => {
                    const active = () => localePolicy().includes(def.code);
                    return (
                      <Button
                        variant={active() ? "default" : "outline"}
                        size="sm"
                        disabled={!canWrite() || upsertMutation.isPending}
                        data-testid={`config-locale-${def.code}`}
                        aria-pressed={active()}
                        onClick={() => {
                          toggleLocale(def.code);
                        }}
                      >
                        <span aria-hidden="true" class="mr-1">
                          {def.flag}
                        </span>
                        {def.label}
                      </Button>
                    );
                  }}
                </For>
              </div>
              <Show when={overriddenKeys().has("i18n.available_locales")}>
                <button
                  type="button"
                  class="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  disabled={!canWrite()}
                  data-testid="reset-i18n.available_locales"
                  onClick={() => {
                    resetOverride("i18n.available_locales");
                  }}
                >
                  <RotateCcw class="h-3 w-3" />
                  {t(msg`Reset to env`)}
                </button>
              </Show>
            </CardContent>
          </Card>

          {/* ── Overrides activos (qué pisa al env AHORA MISMO) ───────── */}
          <Card data-testid="config-overrides">
            <CardHeader>
              <CardTitle>{t(msg`Active overrides`)}</CardTitle>
            </CardHeader>
            <CardContent>
              <Show
                when={overrides().length > 0}
                fallback={
                  <p class="text-sm text-muted-foreground">
                    {t(msg`No overrides. All values come from the environment defaults.`)}
                  </p>
                }
              >
                <div class="space-y-2">
                  <For each={overrides()}>
                    {(row) => (
                      <div class="flex items-center justify-between gap-3 text-sm">
                        <div class="min-w-0 flex items-center gap-2">
                          <span class="font-mono truncate">{row.key}</span>
                          <Badge variant="secondary">{JSON.stringify(row.value)}</Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!canWrite() || deleteMutation.isPending}
                          onClick={() => {
                            resetOverride(row.key);
                          }}
                        >
                          {t(msg`Remove`)}
                        </Button>
                      </div>
                    )}
                  </For>
                </div>
              </Show>
            </CardContent>
          </Card>
        </Show>
      </Show>
    </div>
  );
}
